import Validator from 'validatorjs';

import validOptions from 'core/form/util/validOptionsValidation';

import isCommaSeparatedHostnames from '@kentik/ui-shared/validators/commaSeparatedHostnames';
import bgpPassword from '@kentik/ui-shared/validators/bgpPassword';
import customConditionMax from './alerting/customConditionMax';
import customConditionValue from './alerting/customConditionValue';
import customMaxActivationTimeWindow from './alerting/customMaxActivationTimeWindow';
import customMinActivationTimeWindow from './alerting/customMinActivationTimeWindow';
import after from './after';
import afterDate from './afterDate';
import atLeastOneIfNotAllDevices from './atLeastOneIfNotAllDevices';
import before from './before';
import beforeNow from './beforeNow';
import commaSeparatedASNs from './commaSeparatedASNs';
import commaSeparatedCIDR from './commaSeparatedCIDR';
import commaSeparatedEmails from './commaSeparatedEmails';
import commaSeparatedIPsCIDR from './commaSeparatedIPsCIDR';
import commaSeparatedIPsCIDRWithExcludes from './commaSeparatedIPsCIDRWithExcludes';
import commaSeparatedIPsCIDRwithNetclass from './commaSeparatedIPsCIDRwithNetclass';
import commaSeparatedIPsNoCIDR from './commaSeparatedIPsNoCIDR';
import commaSeparatedIntegers from './commaSeparatedIntegers';
import commaSeparatedMax from './commaSeparatedMax';
import commaSeparatedv4IPsNoCIDR from './commaSeparatedv4IPsNoCIDR';
import commaSeparatedv6IPs from './commaSeparatedv6IPs';
import date from './date';
import dateRangeRequired from './dateRangeRequired';
import email from './email';
import hex from './hex';
import hostname from './hostname';
import htmlSanitary from './htmlSanitary';
import httpHeader from './httpHeader';
import imageFileType from './imageFileType';
import ip from './ip';
import ipMaxSubnet from './ipMaxSubnet';
import ipNoCidr from './ipNoCidr';
import ipNoReverseDNS from './ipNoReverseDNS';
import ipPort from './ipPort';
import ipPublic from './ipPublic';
import ipRFC1918 from './ipRFC1918';
import ipv4 from './ipv4';
import ipv4Port from './ipv4Port';
import ipv4PortUdp from './ipv4PortUdp';
import ipv6 from './ipv6';
import maxDateRange from './maxDateRange';
import maxGenericAggregates from './maxGenericAggregates';
import min_if from './min_if';
import mirrorOrSecondaryMirrorOnly from './mirrorOrSecondaryMirrorOnly';
import newViewCategory from './newViewCategory';
import noConflictingAggregateTypes from './noConflictingAggregateTypes';
import noConflictingUniques from './noConflictingUniques';
import onlyAvailableDimensions from './onlyAvailableDimensions';
import oid from './oid';
import port from './port';
import regexSanitary from './regexSanitary';
import requiredIfIncludes from './requiredIfIncludes';
import requiredIfAny from './requiredIfAny';
import requiredIfMultiple from './requiredIfMultiple';
import safeEmail from './safeEmail';
import uint32 from './uint32';
import url, { urlSingleDomain, safeURL, unsanitaryURL } from './url';
import httpsUrl from './httpsUrl';
import validMirrorAndSecondaryOutsort from './validMirrorAndSecondaryOutsort';
import { endsWith, sameAs, startsWith } from './strings';

import {
  asnValidator,
  bgpAspathValidator,
  bgpCommunityValidator,
  countryValidator,
  interfaceNameValidator,
  macValidator,
  nextHopAsnValidator,
  portValidator,
  protocolValidator,
  tcpFlagValidator,
  vlanValidator
} from './tagValidations';

import {
  asnArrayValidator,
  atLeastOneTagPopulatorCriteria,
  bgpAspathArrayValidator,
  bgpCommunityArrayValidator,
  countryArrayValidator,
  interfaceNameArrayValidator,
  macArrayValidator,
  nextHopAsnArrayValidator,
  portArrayValidator,
  protocolArrayValidator,
  tcpFlagsValidator,
  vlanArrayValidator
} from './tagDetailValidations';

const implicitValidators = {
  requiredIfIncludes,
  requiredIfAny,
  requiredIfMultiple
};

Object.keys(implicitValidators).forEach((name) => {
  const { message, fn } = implicitValidators[name];
  Validator.registerImplicit(name, fn, message);
});

const validators = {
  after,
  afterDate,
  atLeastOneIfNotAllDevices,
  before,
  beforeNow,
  date,
  dateRangeRequired,
  email,
  hostname,
  htmlSanitary,
  httpHeader,
  imageFileType,
  ip,
  ipMaxSubnet,
  ipNoCidr,
  ipNoReverseDNS,
  ipPort,
  ipPublic,
  ipRFC1918,
  ipv4,
  ipv4Port,
  ipv4PortUdp,
  ipv6,
  hex,
  maxDateRange,
  maxGenericAggregates,
  min_if,
  mirrorOrSecondaryMirrorOnly,
  newViewCategory,
  noConflictingAggregateTypes,
  noConflictingUniques,
  onlyAvailableDimensions,
  oid,
  port,
  regexSanitary,
  safeEmail,
  uint32,
  url,
  urlSingleDomain,
  safeURL,
  unsanitaryURL,
  validMirrorAndSecondaryOutsort,
  startsWith,
  endsWith,
  sameAs,
  commaSeparatedEmails,
  commaSeparatedIPsCIDR,
  commaSeparatedIPsCIDRwithNetclass,
  commaSeparatedIPsCIDRWithExcludes,
  commaSeparatedIPsNoCIDR,
  commaSeparatedCIDR,
  commaSeparatedv4IPsNoCIDR,
  commaSeparatedv6IPs,
  commaSeparatedMax,
  commaSeparatedIntegers,
  commaSeparatedASNs,
  isCommaSeparatedHostnames,
  // alerting specific validations
  customConditionMax,
  customConditionValue,
  customMaxActivationTimeWindow,
  customMinActivationTimeWindow,
  // begin legacy tagValidators, can be removed once migrated fully hippo
  portValidator,
  protocolValidator,
  asnValidator,
  nextHopAsnValidator,
  tcpFlagValidator,
  countryValidator,
  macValidator,
  bgpCommunityValidator,
  bgpAspathValidator,
  interfaceNameValidator,
  vlanValidator,
  // end legacy tagValidators
  // begin new tagDetailValidators
  portArrayValidator,
  protocolArrayValidator,
  asnArrayValidator,
  nextHopAsnArrayValidator,
  tcpFlagsValidator,
  countryArrayValidator,
  macArrayValidator,
  bgpCommunityArrayValidator,
  bgpAspathArrayValidator,
  interfaceNameArrayValidator,
  vlanArrayValidator,
  atLeastOneTagPopulatorCriteria,
  // end new tagDetailValidators
  validOptions,
  bgpPassword,
  httpsUrl
};

export default function initValidators() {
  Object.keys(validators).forEach((name) => {
    const { message, fn } = validators[name];
    Validator.register(name, fn, message);
  });
}
